// Re-export CatalogItemType from @pushly/models for convenience
import { CatalogItemType } from '@pushly/models/lib/enums'
export { CatalogItemType }

/**
 * Base interface for all catalog item API responses.
 * Maps to the common fields returned by the Sailfish catalog items API.
 *
 * Note: Properties use snake_case to match the API response format.
 * The catalog service uses skipCaseConversion to bypass the axios
 * response interceptor's automatic camelCase conversion.
 */
export interface CatalogItemResponseBase {
    id: number
    item_id: string
    /**
     * Discriminator field enabling TypeScript narrowing over the `CatalogItemResponse` union.
     * Overridden on each concrete response type with a specific literal value.
     */
    item_type: CatalogItemType
    name: string
    description?: string | null
    image_url?: string | null
    url?: string | null
    updated_at?: string | null
}

/**
 * Product catalog item response from API.
 * Maps to GetCatalogProductResponse from @pushly/models.
 */
export interface ProductCatalogItemResponse extends CatalogItemResponseBase {
    item_type: CatalogItemType.PRODUCT
    price?: number | null
    price_currency?: string | null
    availability?: string | null
    brand_name?: string | null
    category_path?: string[] | null
}

/**
 * Event catalog item response from API.
 * Maps to GetCatalogEventResponse from @pushly/models.
 */
export interface EventCatalogItemResponse extends CatalogItemResponseBase {
    item_type: CatalogItemType.TICKETED_EVENT
    price?: number | null
    price_currency?: string | null
    date_start?: string | null
    date_end?: string | null
    location_name?: string | null
    location_city?: string | null
    location_region?: string | null
    location_country?: string | null
    performer_name?: string | null
    genre_major?: string | null
    genre_minor?: string | null
    availability_primary?: string | null
    availability_resale?: string | null
}

/**
 * Recipe catalog item response from API.
 * Maps to GetCatalogRecipeResponse from @pushly/models.
 */
export interface RecipeCatalogItemResponse extends CatalogItemResponseBase {
    item_type: CatalogItemType.RECIPE
    prep_time?: string | null
    cook_time?: string | null
    total_time?: string | null
    aggregate_rating?: string | null
    author?: string | null
    recipe_category?: string | null
    recipe_cuisine?: string | null
    keywords?: string[] | null
}

/**
 * Discriminated union of all catalog item response types.
 *
 * The `item_type` field acts as the discriminator — narrow via the type guards
 * below or via `switch (item.item_type)`.
 */
export type CatalogItemResponse = ProductCatalogItemResponse | EventCatalogItemResponse | RecipeCatalogItemResponse

/**
 * Type guard narrowing a `CatalogItemResponse` to `ProductCatalogItemResponse`.
 */
export function isProductResponse(item: CatalogItemResponse): item is ProductCatalogItemResponse {
    return item.item_type === CatalogItemType.PRODUCT
}

/**
 * Type guard narrowing a `CatalogItemResponse` to `EventCatalogItemResponse`.
 */
export function isEventResponse(item: CatalogItemResponse): item is EventCatalogItemResponse {
    return item.item_type === CatalogItemType.TICKETED_EVENT
}

/**
 * Type guard narrowing a `CatalogItemResponse` to `RecipeCatalogItemResponse`.
 */
export function isRecipeResponse(item: CatalogItemResponse): item is RecipeCatalogItemResponse {
    return item.item_type === CatalogItemType.RECIPE
}
